/**
 * App.tsx — Application root.
 *
 * This file is now purely concerned with providers and routing.
 * Layout shell, sidebar, and profile avatar have moved to
 * components/layout/AppLayout.tsx.
 *
 * Before: ~384 lines with inline Sidebar/ProfileAvatar/DashboardLayout.
 * After: ~70 lines.
 */

import { BrowserRouter, Routes, Route } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AdminProvider } from './AdminContext';
import { AppLayout } from './components/layout/AppLayout';

// Public marketing / legal pages
import Landing from './pages/public/Landing';
import RequestDemo from './pages/public/RequestDemo';
import FAQ from './pages/public/FAQ';
import ApiDocs from './pages/public/legal/ApiDocs';
import PrivacyPolicy from './pages/public/legal/PrivacyPolicy';
import TermsOfService from './pages/public/legal/TermsOfService';
import CookiePolicy from './pages/public/legal/CookiePolicy';

// Account / authentication
import Login from './pages/account/Login';
import Callback from './pages/account/Callback';
import Account from './pages/account/Account';

// Client-facing pages (dashboard, records, review, gaps, reports, upload)
import MatchResults from './pages/client/MatchResults';
import MyRecords from './pages/client/MyRecords';
import MatchedOutlets from './pages/client/MatchedOutlets';
import ReviewQueue from './pages/client/ReviewQueue';
import DistributionGaps from './pages/client/DistributionGaps';
import Reports from './pages/client/Reports';
import Upload from './pages/client/Upload';

// Registry pages — outlet search/detail, map
import CensusRegistry from './pages/registry/CensusRegistry';
import OutletDetail from './pages/registry/OutletDetail';
import MapView from './pages/registry/MapView';

// Admin pages — tools, users, audit, demo, client management
import AdminTools from './pages/admin/AdminTools';
import UserManagement from './pages/admin/UserManagement';
import AuditLog from './pages/admin/AuditLog';
import DemoRequests from './pages/admin/DemoRequests';
import ClientsDatabase from './pages/admin/ClientsDatabase';
import ClientRecordsDatabase from './pages/admin/ClientRecordsDatabase';

// Cross-cutting (export, temporal, API docs)
import Export from './pages/shared/Export';
import Temporal from './pages/shared/Temporal';

const fallbackStyle = {
  padding: '2rem',
  color: '#EF4444',
  fontFamily: "'DM Sans', sans-serif",
};

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<p style={fallbackStyle}>Something went wrong. Our team has been notified.</p>}>
      <ErrorBoundary>
        <AdminProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <Routes>
                {/* Public */}
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/callback" element={<Callback />} />
                <Route path="/api-docs" element={<ApiDocs />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/cookies" element={<CookiePolicy />} />
                <Route path="/request-demo" element={<RequestDemo />} />
                <Route path="/faq" element={<FAQ />} />

                {/* Logged in — wrapped in AppLayout */}
                <Route element={<AppLayout />}>
                  <Route path="/dashboard" element={<MatchResults />} />
                  <Route path="/my-records" element={<MyRecords />} />
                  <Route path="/matched-outlets" element={<MatchedOutlets />} />
                  <Route path="/admin-tools" element={<AdminTools />} />
                  <Route path="/clients" element={<ClientsDatabase />} />
                  <Route path="/client-records" element={<ClientRecordsDatabase />} />
                  <Route path="/census-database" element={<CensusRegistry />} />
                  <Route path="/outlet/:censusId" element={<OutletDetail />} />
                  <Route path="/review-queue" element={<ReviewQueue />} />
                  <Route path="/distribution-gaps" element={<DistributionGaps />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/registry-map" element={<MapView />} />
                  <Route path="/market-intelligence" element={<Temporal />} />
                  <Route path="/upload" element={<Upload />} />
                  <Route path="/export" element={<Export />} />
                  <Route path="/demo-requests" element={<DemoRequests />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/user-management" element={<UserManagement />} />
                  <Route path="/audit-log" element={<AuditLog />} />
                </Route>
              </Routes>
            </ErrorBoundary>
          </BrowserRouter>
        </AdminProvider>
      </ErrorBoundary>
    </Sentry.ErrorBoundary>
  );
}

export default App;
